import React, { useState, useMemo, useCallback } from 'react';
import { useCRM } from '../utils/CRMContext';
import { useAuth } from '../utils/AuthContext';
import { Lead, LeadSource, LeadStatus } from '../types';
import { format } from 'date-fns';
import { geocodeAddress } from '../utils/geocoding';
import {
  Wrench,
  Search,
  BarChart3,
  User,
  Mail,
  Trash2,
  ChevronLeft,
  MapPin,
  Globe,
  AlertTriangle,
  Check,
  X,
  Save,
  Pencil,
} from 'lucide-react';
import './LeadTools.css';

const LEAD_SOURCES: LeadSource[] = [
  'web-form',
  'facebook-form', 
  'instagram-form',
  'ebook',
  'instagram-follow',
  'facebook-follow',
  'b2b-visit',
  'cold-call',
  'call-in',
  'referral',
  'unspecified'
];

const LEAD_STATUSES: LeadStatus[] = [
  'new',
  'contacted',
  'qualified',
  'lost',
  'nurture',
  'out-of-scope',
];

interface DuplicateGroup {
  key: string;
  leads: Lead[];
  type: 'name' | 'email';
}

type QualityIssue = 'missing-email' | 'not-geocoded' | 'name-duplicates' | 'email-duplicates' | null;

const LeadTools: React.FC = () => {
  const { user } = useAuth();
  const { leads, deleteLead, updateLead } = useCRM();
  const [activeTab, setActiveTab] = useState<'duplicates' | 'stats' | 'quality'>('duplicates');
  const [selectedGroup, setSelectedGroup] = useState<DuplicateGroup | null>(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState<string | null>(null);
  const [editingSource, setEditingSource] = useState<string | null>(null);
  const [savingSource, setSavingSource] = useState<string | null>(null);
  const [selectedQualityIssue, setSelectedQualityIssue] = useState<QualityIssue>(null);
  const [editingLead, setEditingLead] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<Lead>>({});
  const [geocodingProgress, setGeocodingProgress] = useState<{ current: number; total: number; status: string } | null>(null);
  const [geocodingResults, setGeocodingResults] = useState<{ success: number; failed: number; skipped: number } | null>(null);

  // Find duplicates by name (case-insensitive)
  const nameDuplicates = useMemo(() => {
    const nameMap = new Map<string, Lead[]>();
    
    leads.forEach(lead => {
      const fullName = `${lead.firstName || ''} ${lead.lastName || ''}`.toLowerCase().trim();
      if (fullName && fullName !== 'unknown' && fullName !== 'unknown unknown') {
        const existing = nameMap.get(fullName) || [];
        existing.push(lead);
        nameMap.set(fullName, existing);
      }
    });
    
    // Filter to only groups with more than one lead
    const duplicates: DuplicateGroup[] = [];
    nameMap.forEach((groupLeads, key) => {
      if (groupLeads.length > 1) {
        duplicates.push({ key, leads: groupLeads, type: 'name' });
      }
    });
    
    // Sort by number of duplicates (most first)
    return duplicates.sort((a, b) => b.leads.length - a.leads.length);
  }, [leads]);

  // Find duplicates by email (case-insensitive)
  const emailDuplicates = useMemo(() => {
    const emailMap = new Map<string, Lead[]>();
    
    leads.forEach(lead => {
      const email = (lead.email || '').toLowerCase().trim();
      if (email && email !== 'unknown@import.local' && email.includes('@')) {
        const existing = emailMap.get(email) || [];
        existing.push(lead);
        emailMap.set(email, existing);
      }
    });
    
    // Filter to only groups with more than one lead
    const duplicates: DuplicateGroup[] = [];
    emailMap.forEach((groupLeads, key) => {
      if (groupLeads.length > 1) {
        duplicates.push({ key, leads: groupLeads, type: 'email' });
      }
    });
    
    // Sort by number of duplicates (most first)
    return duplicates.sort((a, b) => b.leads.length - a.leads.length);
  }, [leads]);

  // Stats
  const stats = useMemo(() => {
    const totalLeads = leads.length;
    const withEmail = leads.filter(l => l.email && l.email !== 'unknown@import.local').length;
    const withPhone = leads.filter(l => l.phone && String(l.phone).trim()).length;
    const withLocation = leads.filter(l => l.location && String(l.location).trim()).length;
    const geocoded = leads.filter(l => l.geocoded === true).length;
    const archived = leads.filter(l => l.archived === true).length;
    
    // Source breakdown
    const sourceMap = new Map<string, number>();
    leads.forEach(lead => {
      const source = lead.source || 'unspecified';
      sourceMap.set(source, (sourceMap.get(source) || 0) + 1);
    });
    
    // Status breakdown
    const statusMap = new Map<string, number>();
    leads.forEach(lead => {
      const status = lead.status || 'new';
      statusMap.set(status, (statusMap.get(status) || 0) + 1);
    });
    
    return {
      totalLeads,
      withEmail,
      withPhone,
      withLocation,
      geocoded,
      archived,
      sources: Array.from(sourceMap.entries()).sort((a, b) => b[1] - a[1]),
      statuses: Array.from(statusMap.entries()).sort((a, b) => b[1] - a[1]),
      nameDuplicateGroups: nameDuplicates.length,
      nameDuplicateLeads: nameDuplicates.reduce((sum, g) => sum + g.leads.length, 0),
      emailDuplicateGroups: emailDuplicates.length,
      emailDuplicateLeads: emailDuplicates.reduce((sum, g) => sum + g.leads.length, 0),
    };
  }, [leads, nameDuplicates, emailDuplicates]);

  const handleDeleteLead = async (leadId: string) => {
    try {
      await deleteLead(leadId);
      setShowConfirmDelete(null);
      // Update selected group if we deleted from it
      if (selectedGroup) {
        const updatedLeads = selectedGroup.leads.filter(l => l.id !== leadId);
        if (updatedLeads.length <= 1) {
          setSelectedGroup(null);
        } else {
          setSelectedGroup({ ...selectedGroup, leads: updatedLeads });
        }
      }
    } catch (err) {
      console.error('Error deleting lead:', err);
      alert('Error deleting lead');
    }
  };

  const handleUpdateSource = async (leadId: string, newSource: LeadSource) => {
    setSavingSource(leadId);
    try {
      await updateLead(leadId, { source: newSource });
      setEditingSource(null);
      // Update the lead in selected group
      if (selectedGroup) {
        const updatedLeads = selectedGroup.leads.map(l => 
          l.id === leadId ? { ...l, source: newSource } : l
        );
        setSelectedGroup({ ...selectedGroup, leads: updatedLeads });
      }
    } catch (err) {
      console.error('Error updating lead source:', err);
      alert('Error updating source');
    } finally {
      setSavingSource(null);
    }
  };

  // Get leads with quality issues
  const qualityIssueLeads = useMemo(() => {
    const missingEmail = leads.filter(l => !l.email || l.email === 'unknown@import.local');
    const notGeocoded = leads.filter(l => {
      if (l.geocoded === true) return false;
      if (!l.coordinates) return true;
      // Check for default coordinates (50 miles off Norfolk)
      if (l.coordinates.lat === 36.85 && l.coordinates.lng === -75.4) return true;
      if (l.coordinates.lat === 36.5 && l.coordinates.lng === -71.58) return true;
      return false;
    });
    return { missingEmail, notGeocoded };
  }, [leads]);

  // Start editing a lead
  const startEditingLead = (lead: Lead) => {
    setEditingLead(lead.id!);
    setEditForm({
      firstName: lead.firstName,
      lastName: lead.lastName,
      email: lead.email,
      phone: lead.phone || '',
      location: lead.location || '',
      source: lead.source,
      status: lead.status,
    });
  };

  // Save lead edits
  const saveLeadEdits = async (leadId: string) => {
    try {
      await updateLead(leadId, editForm);
      setEditingLead(null);
      setEditForm({});
    } catch (err) {
      console.error('Error saving lead:', err);
      alert('Error saving lead');
    }
  };

  // Run geocoding on all non-geocoded leads
  const runBulkGeocoding = useCallback(async () => {
    const toGeocode = qualityIssueLeads.notGeocoded.filter(l => l.location && l.location.trim());
    
    if (toGeocode.length === 0) {
      alert('No leads with locations to geocode');
      return;
    }

    const confirmed = window.confirm(
      `This will attempt to geocode ${toGeocode.length} leads. This may take a few minutes. Continue?`
    );
    if (!confirmed) return;

    setGeocodingProgress({ current: 0, total: toGeocode.length, status: 'Starting...' });
    setGeocodingResults(null);

    let success = 0;
    let failed = 0;
    let skipped = 0;

    for (let i = 0; i < toGeocode.length; i++) {
      const lead = toGeocode[i];
      setGeocodingProgress({ 
        current: i + 1, 
        total: toGeocode.length, 
        status: `Geocoding: ${lead.firstName} ${lead.lastName} - ${lead.location?.substring(0, 30)}...`
      });

      if (!lead.location || !lead.id) {
        skipped++;
        continue;
      }

      try {
        const coords = await geocodeAddress(lead.location);
        if (coords) {
          await updateLead(lead.id, { 
            coordinates: coords, 
            geocoded: true 
          });
          success++;
        } else {
          failed++;
        }
      } catch (err) {
        console.warn('Geocoding failed for:', lead.location, err);
        failed++;
      }

      // Small delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    setGeocodingProgress(null);
    setGeocodingResults({ success, failed, skipped });
  }, [qualityIssueLeads.notGeocoded, updateLead]);

  if (!user) {
    return (
      <div className="lead-tools">
        <h1>Lead Analysis Tools</h1>
        <p>Please log in to access these tools.</p>
      </div>
    );
  }

  return (
    <div className="lead-tools">
      <div className="tools-header">
        <h1><Wrench size={24} style={{ verticalAlign: 'middle', marginRight: 8 }} />Lead Analysis Tools</h1>
        <p>Analyze and clean up your lead database</p>
      </div>

      <div className="tools-tabs">
        <button 
          className={`tab-btn ${activeTab === 'duplicates' ? 'active' : ''}`}
          onClick={() => setActiveTab('duplicates')}
        >
          <Search size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Duplicate Finder
        </button>
        <button 
          className={`tab-btn ${activeTab === 'quality' ? 'active' : ''}`}
          onClick={() => setActiveTab('quality')}
        >
          <Wrench size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Data Quality
        </button>
        <button 
          className={`tab-btn ${activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('stats')}
        >
          <BarChart3 size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Database Stats
        </button>
      </div>

      {activeTab === 'duplicates' && (
        <div className="duplicates-section">
          <div className="duplicates-summary">
            <div className="summary-card name-card">
              <div className="summary-icon"><User size={24} /></div>
              <div className="summary-content">
                <div className="summary-number">{stats.nameDuplicateGroups}</div>
                <div className="summary-label">Name Duplicate Groups</div>
                <div className="summary-detail">{stats.nameDuplicateLeads} total leads affected</div>
              </div>
            </div>
            <div className="summary-card email-card">
              <div className="summary-icon"><Mail size={24} /></div>
              <div className="summary-content">
                <div className="summary-number">{stats.emailDuplicateGroups}</div>
                <div className="summary-label">Email Duplicate Groups</div>
                <div className="summary-detail">{stats.emailDuplicateLeads} total leads affected</div>
              </div>
            </div>
          </div>

          <div className="duplicates-layout">
            <div className="duplicates-list">
              <h3>Name Duplicates</h3>
              {nameDuplicates.length === 0 ? (
                <p className="no-duplicates"><Check size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />No name duplicates found</p>
              ) : (
                <div className="duplicate-groups">
                  {nameDuplicates.map((group, i) => (
                    <div 
                      key={`name-${i}`}
                      className={`duplicate-group ${selectedGroup === group ? 'selected' : ''}`}
                      onClick={() => setSelectedGroup(group)}
                    >
                      <span className="group-name">{group.key}</span>
                      <span className="group-count">{group.leads.length} leads</span>
                    </div>
                  ))}
                </div>
              )}

              <h3>Email Duplicates</h3>
              {emailDuplicates.length === 0 ? (
                <p className="no-duplicates"><Check size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />No email duplicates found</p>
              ) : (
                <div className="duplicate-groups">
                  {emailDuplicates.map((group, i) => (
                    <div 
                      key={`email-${i}`}
                      className={`duplicate-group ${selectedGroup === group ? 'selected' : ''}`}
                      onClick={() => setSelectedGroup(group)}
                    >
                      <span className="group-name">{group.key}</span>
                      <span className="group-count">{group.leads.length} leads</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="duplicate-detail">
              {selectedGroup ? (
                <>
                  <h3>
                    {selectedGroup.type === 'name' ? <User size={18} style={{ verticalAlign: 'middle', marginRight: 4 }} /> : <Mail size={18} style={{ verticalAlign: 'middle', marginRight: 4 }} />}
                    Duplicate: {selectedGroup.key}
                  </h3>
                  <p className="detail-hint">
                    Compare the entries below and delete duplicates. Keep the most complete record.
                  </p>
                  <div className="duplicate-leads">
                    {selectedGroup.leads.map((lead, i) => (
                      <div key={lead.id} className="duplicate-lead-card">
                        <div className="lead-card-header">
                          <span className="lead-number">#{i + 1}</span>
                          {editingSource === lead.id ? (
                            <select
                              className="source-select"
                              value={lead.source}
                              onChange={(e) => handleUpdateSource(lead.id!, e.target.value as LeadSource)}
                              disabled={savingSource === lead.id}
                              autoFocus
                              onBlur={() => setEditingSource(null)}
                            >
                              {LEAD_SOURCES.map(src => (
                                <option key={src} value={src}>{src}</option>
                              ))}
                            </select>
                          ) : (
                            <span 
                              className="lead-source clickable" 
                              style={{ 
                                backgroundColor: lead.source === 'facebook-form' ? '#4267B2' : 
                                               lead.source === 'instagram-form' ? '#E1306C' : 
                                               lead.source === 'web-form' ? '#F5A623' : '#999',
                                color: 'white'
                              }}
                              onClick={() => setEditingSource(lead.id!)}
                              title="Click to change source"
                            >
                              {lead.source} ✎
                            </span>
                          )}
                        </div>
                        <div className="lead-card-body">
                          <div className="lead-field">
                            <label>Name:</label>
                            <span>{lead.firstName} {lead.lastName}</span>
                          </div>
                          <div className="lead-field">
                            <label>Email:</label>
                            <span>{lead.email || '—'}</span>
                          </div>
                          <div className="lead-field">
                            <label>Phone:</label>
                            <span>{lead.phone || '—'}</span>
                          </div>
                          <div className="lead-field">
                            <label>Location:</label>
                            <span>{lead.location || '—'}</span>
                          </div>
                          <div className="lead-field">
                            <label>Status:</label>
                            <span>{lead.status}</span>
                          </div>
                          <div className="lead-field">
                            <label>Created:</label>
                            <span>{format(lead.createdAt, 'MMM d, yyyy h:mm a')}</span>
                          </div>
                          {lead.metaAdCampaign && (
                            <div className="lead-field">
                              <label>Campaign:</label>
                              <span>{lead.metaAdCampaign}</span>
                            </div>
                          )}
                          {lead.notes && (
                            <div className="lead-field notes">
                              <label>Notes:</label>
                              <span>{lead.notes}</span>
                            </div>
                          )}
                        </div>
                        <div className="lead-card-actions">
                          {showConfirmDelete === lead.id ? (
                            <div className="confirm-delete">
                              <span>Delete this lead?</span>
                              <button 
                                className="confirm-yes"
                                onClick={() => handleDeleteLead(lead.id!)}
                              >
                                Yes, Delete
                              </button>
                              <button 
                                className="confirm-no"
                                onClick={() => setShowConfirmDelete(null)}
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button 
                              className="delete-btn"
                              onClick={() => setShowConfirmDelete(lead.id!)}
                            >
                              <Trash2 size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />Delete This Duplicate
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="no-selection">
                  <span className="no-selection-icon"><ChevronLeft size={24} /></span>
                  <p>Select a duplicate group from the list to compare entries</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'quality' && (
        <div className="quality-section">
          <div className="quality-cards">
            <div 
              className={`quality-card clickable ${selectedQualityIssue === 'missing-email' ? 'selected' : ''}`}
              onClick={() => setSelectedQualityIssue(selectedQualityIssue === 'missing-email' ? null : 'missing-email')}
            >
              <div className="quality-icon"><Mail size={24} /></div>
              <div className="quality-info">
                <div className="quality-count">{qualityIssueLeads.missingEmail.length}</div>
                <div className="quality-label">Missing Email</div>
              </div>
            </div>
            <div 
              className={`quality-card clickable ${selectedQualityIssue === 'not-geocoded' ? 'selected' : ''}`}
              onClick={() => setSelectedQualityIssue(selectedQualityIssue === 'not-geocoded' ? null : 'not-geocoded')}
            >
              <div className="quality-icon"><MapPin size={24} /></div>
              <div className="quality-info">
                <div className="quality-count">{qualityIssueLeads.notGeocoded.length}</div>
                <div className="quality-label">Not Geocoded</div>
              </div>
            </div>
          </div>

          {selectedQualityIssue === 'not-geocoded' && (
            <div className="geocoding-tools">
              <button 
                className="geocode-all-btn"
                onClick={runBulkGeocoding}
                disabled={geocodingProgress !== null}
              >
                <Globe size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Run Geocoding on All ({qualityIssueLeads.notGeocoded.filter(l => l.location).length} with locations)
              </button>
              
              {geocodingProgress && (
                <div className="geocoding-progress">
                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${(geocodingProgress.current / geocodingProgress.total) * 100}%` }}
                    />
                  </div>
                  <div className="progress-text">
                    {geocodingProgress.current} / {geocodingProgress.total} - {geocodingProgress.status}
                  </div>
                </div>
              )}

              {geocodingResults && (
                <div className="geocoding-results">
                  <span className="result-success"><Check size={14} style={{ verticalAlign: 'middle', marginRight: 2 }} />{geocodingResults.success} geocoded</span>
                  <span className="result-failed"><X size={14} style={{ verticalAlign: 'middle', marginRight: 2 }} />{geocodingResults.failed} failed</span>
                  <span className="result-skipped">{geocodingResults.skipped} skipped</span>
                </div>
              )}
            </div>
          )}

          {selectedQualityIssue && (
            <div className="quality-leads-list">
              <h3>
                {selectedQualityIssue === 'missing-email' ? <><Mail size={18} style={{ verticalAlign: 'middle', marginRight: 4 }} />Leads Missing Email</> : <><MapPin size={18} style={{ verticalAlign: 'middle', marginRight: 4 }} />Leads Not Geocoded</>}
                <span className="lead-count">
                  ({selectedQualityIssue === 'missing-email' 
                    ? qualityIssueLeads.missingEmail.length 
                    : qualityIssueLeads.notGeocoded.length} leads)
                </span>
              </h3>
              <div className="quality-leads-table-container">
                <table className="quality-leads-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Location</th>
                      <th>Source</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedQualityIssue === 'missing-email' 
                      ? qualityIssueLeads.missingEmail 
                      : qualityIssueLeads.notGeocoded
                    ).slice(0, 50).map(lead => (
                      <tr key={lead.id} className={editingLead === lead.id ? 'editing' : ''}>
                        {editingLead === lead.id ? (
                          <>
                            <td>
                              <input 
                                type="text"
                                value={editForm.firstName || ''}
                                onChange={e => setEditForm({...editForm, firstName: e.target.value})}
                                placeholder="First"
                                className="edit-input small"
                              />
                              <input 
                                type="text"
                                value={editForm.lastName || ''}
                                onChange={e => setEditForm({...editForm, lastName: e.target.value})}
                                placeholder="Last"
                                className="edit-input small"
                              />
                            </td>
                            <td>
                              <input 
                                type="email"
                                value={editForm.email || ''}
                                onChange={e => setEditForm({...editForm, email: e.target.value})}
                                placeholder="Email"
                                className="edit-input"
                              />
                            </td>
                            <td>
                              <input 
                                type="text"
                                value={editForm.phone || ''}
                                onChange={e => setEditForm({...editForm, phone: e.target.value})}
                                placeholder="Phone"
                                className="edit-input"
                              />
                            </td>
                            <td>
                              <input 
                                type="text"
                                value={editForm.location || ''}
                                onChange={e => setEditForm({...editForm, location: e.target.value})}
                                placeholder="Location"
                                className="edit-input"
                              />
                            </td>
                            <td>
                              <select 
                                value={editForm.source || 'unspecified'}
                                onChange={e => setEditForm({...editForm, source: e.target.value as LeadSource})}
                                className="edit-select"
                              >
                                {LEAD_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                              </select>
                            </td>
                            <td>
                              <select 
                                value={editForm.status || 'new'}
                                onChange={e => setEditForm({...editForm, status: e.target.value as LeadStatus})}
                                className="edit-select"
                              >
                                {LEAD_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                              </select>
                            </td>
                            <td className="actions-cell">
                              <button className="save-btn" onClick={() => saveLeadEdits(lead.id!)}><Save size={14} /></button>
                              <button className="cancel-btn" onClick={() => { setEditingLead(null); setEditForm({}); }}><X size={14} /></button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td>{lead.firstName} {lead.lastName}</td>
                            <td className={!lead.email || lead.email === 'unknown@import.local' ? 'missing' : ''}>
                              {lead.email || '—'}
                            </td>
                            <td>{lead.phone || '—'}</td>
                            <td className={!lead.location ? 'missing' : ''}>
                              {lead.location || '—'}
                              {lead.geocoded && <span className="geocoded-badge"><Check size={12} /></span>}
                            </td>
                            <td>{lead.source}</td>
                            <td>{lead.status}</td>
                            <td className="actions-cell">
                              <button className="edit-btn" onClick={() => startEditingLead(lead)}><Pencil size={14} /></button>
                              <button 
                                className="delete-btn-small" 
                                onClick={() => {
                                  if (window.confirm('Delete this lead?')) {
                                    deleteLead(lead.id!);
                                  }
                                }}
                              ><Trash2 size={14} /></button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(selectedQualityIssue === 'missing-email' 
                  ? qualityIssueLeads.missingEmail.length 
                  : qualityIssueLeads.notGeocoded.length) > 50 && (
                  <div className="table-note">Showing first 50 leads. Fix these first, then refresh to see more.</div>
                )}
              </div>
            </div>
          )}

          {!selectedQualityIssue && (
            <div className="quality-placeholder">
              <span className="placeholder-icon"><AlertTriangle size={24} /></span>
              <p>Click on an issue above to view and fix affected leads</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'stats' && (
        <div className="stats-section">
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-number">{stats.totalLeads}</div>
              <div className="stat-label">Total Leads</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{stats.withEmail}</div>
              <div className="stat-label">With Email</div>
              <div className="stat-percent">{((stats.withEmail / stats.totalLeads) * 100).toFixed(1)}%</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{stats.withPhone}</div>
              <div className="stat-label">With Phone</div>
              <div className="stat-percent">{((stats.withPhone / stats.totalLeads) * 100).toFixed(1)}%</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{stats.geocoded}</div>
              <div className="stat-label">Geocoded</div>
              <div className="stat-percent">{((stats.geocoded / stats.totalLeads) * 100).toFixed(1)}%</div>
            </div>
            <div className="stat-card">
              <div className="stat-number">{stats.archived}</div>
              <div className="stat-label">Archived</div>
            </div>
          </div>

          <div className="breakdown-section">
            <div className="breakdown-card">
              <h3>By Source</h3>
              <div className="breakdown-list">
                {stats.sources.map(([source, count]) => (
                  <div key={source} className="breakdown-item">
                    <span className="breakdown-name">{source}</span>
                    <span className="breakdown-count">{count}</span>
                    <div className="breakdown-bar">
                      <div 
                        className="breakdown-fill"
                        style={{ width: `${(count / stats.totalLeads) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="breakdown-card">
              <h3>By Status</h3>
              <div className="breakdown-list">
                {stats.statuses.map(([status, count]) => (
                  <div key={status} className="breakdown-item">
                    <span className="breakdown-name">{status}</span>
                    <span className="breakdown-count">{count}</span>
                    <div className="breakdown-bar">
                      <div 
                        className="breakdown-fill status"
                        style={{ width: `${(count / stats.totalLeads) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="data-quality-section">
            <h3>Data Quality Issues</h3>
            <div className="quality-items">
              <div className={`quality-item ${stats.nameDuplicateGroups > 0 ? 'warning' : 'good'}`}>
                {stats.nameDuplicateGroups > 0 ? <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> : <Check size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />}
                {stats.nameDuplicateGroups} name duplicate groups
              </div>
              <div className={`quality-item ${stats.emailDuplicateGroups > 0 ? 'warning' : 'good'}`}>
                {stats.emailDuplicateGroups > 0 ? <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> : <Check size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />}
                {stats.emailDuplicateGroups} email duplicate groups
              </div>
              <div className={`quality-item ${stats.totalLeads - stats.withEmail > 10 ? 'warning' : 'good'}`}>
                {stats.totalLeads - stats.withEmail > 10 ? <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> : <Check size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />}
                {stats.totalLeads - stats.withEmail} leads missing email
              </div>
              <div className={`quality-item ${stats.totalLeads - stats.geocoded > stats.totalLeads * 0.2 ? 'warning' : 'good'}`}>
                {stats.totalLeads - stats.geocoded > stats.totalLeads * 0.2 ? <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} /> : <Check size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />}
                {stats.totalLeads - stats.geocoded} leads not geocoded
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadTools;
